import React from 'react';
import { Check, ExternalLink, Pencil, Trash } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDirectTheme } from '../../../hooks/useDirectTheme';
import { Z_LAYERS } from '../../../constants/zLayers';
import type { MenuButtonProps, VerticalMenuProps } from './types';

const MenuButton: React.FC<MenuButtonProps> = ({
  icon: Icon,
  onClick,
  hoverColor,
  label,
  isDelete,
}) => {
  const { getDirectColor } = useDirectTheme();

  return (
    <li role="none">
      <motion.button
        className={`text-gray-400 block w-full p-1.5 ${isDelete ? 'font-bold' : ''}`}
        whileHover={{
          scale: 1.05,
          color: hoverColor,
        }}
        whileTap={{ scale: 0.95 }}
        onClick={onClick}
        initial={{ color: isDelete ? '#CC0000' : getDirectColor('actionIcon') }}
        style={{
          fontSize: isDelete ? '1.1rem' : 'inherit',
          textShadow: isDelete ? '0px 0px 1px rgba(0,0,0,0.2)' : 'none',
          backgroundColor: isDelete ? getDirectColor('menuBackground') : 'transparent',
        }}
        title={label}
        aria-label={label}
        role="menuitem"
      >
        <span className="block w-4 mx-auto" aria-hidden="true">
          <Icon className="w-full h-full" />
        </span>
      </motion.button>
    </li>
  );
};

export const VerticalMenu: React.FC<VerticalMenuProps> = ({
  onUse,
  onOpenInChatGPT,
  onEdit,
  onDelete,
}) => {
  const { getDirectColor } = useDirectTheme();

  return (
    <menu
      className="w-[47px] rounded-xl shadow-lg relative isolate"
      style={{
        backgroundColor: getDirectColor('menuBackground'),
        zIndex: Z_LAYERS.MENU_BUTTON,
      }}
      role="menu"
      aria-label="Prompt actions"
    >
      <ul role="none">
        <MenuButton icon={Check} onClick={onUse} hoverColor="#29B8FF" label="Use Prompt" />
        <MenuButton
          icon={ExternalLink}
          onClick={onOpenInChatGPT}
          hoverColor="#7ED321"
          label="Open in ChatGPT"
        />
        <MenuButton icon={Pencil} onClick={onEdit} hoverColor="#F5A623" label="Edit" />
        <MenuButton
          icon={Trash}
          onClick={onDelete}
          hoverColor="#CC0000"
          label="Delete"
          isDelete={true}
        />
      </ul>
    </menu>
  );
};
